import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
	Badge,
	Box,
	Button,
	Card,
	CardBody,
	CardHeader,
	Flex,
	Stack,
	Table,
	Tbody,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue,
} from "@chakra-ui/react";

import { DOCUMENT_PAGE_SIZE } from "../../../utils/ownerDocuments";

import DocumentsTableRow from "./DocumentsTableRow";
import ReportsTableRow from "./ReportsTableRow";

function DocumentsGroup({ label, count, colorScheme, defaultOpen, children }) {
	const [isOpen, setIsOpen] = useState(Boolean(defaultOpen));
	const groupBg = useColorModeValue("gray.50", "whiteAlpha.100");
	const { t } = useTranslation();

	return (
		<Box borderWidth="1px" borderRadius="md">
			<Flex
				as="button"
				type="button"
				width="100%"
				alignItems="center"
				gap={2}
				px={3}
				py={2}
				bg={groupBg}
				borderTopRadius="md"
				aria-expanded={isOpen}
				aria-label={isOpen ? t("documents.collapseGroup", { label }) : t("documents.expandGroup", { label })}
				onClick={() => setIsOpen(!isOpen)}
			>
				<Box as={isOpen ? ChevronDownIcon : ChevronRightIcon} boxSize={4} />
				<Text fontWeight="semibold" fontSize="sm">
					{label}
				</Text>
				<Badge colorScheme={colorScheme || "gray"}>{count}</Badge>
			</Flex>
			{isOpen && <Box px={3} pb={3}>{children}</Box>}
		</Box>
	);
}

function DocumentsTable({ documents, propertyId }) {
	const { t } = useTranslation();
	const [visibleCount, setVisibleCount] = useState(DOCUMENT_PAGE_SIZE);
	const visible = documents.slice(0, visibleCount);
	const hiddenCount = documents.length - visible.length;

	return (
		<Box overflowX="auto">
			<Table variant="simple" size="sm">
				<Thead>
					<Tr>
						<Th pl="0px">{t("table.addedDate")}</Th>
						<Th>{t("table.name")}</Th>
						<Th>{t("table.type")}</Th>
						<Th>{t("table.number")}</Th>
						<Th>{t("table.startingDate")}</Th>
						<Th>{t("table.expirationDate")}</Th>
						<Th />
					</Tr>
				</Thead>
				<Tbody>
					{visible.map((row, index) => (
						<DocumentsTableRow key={row.id || index} row={row} propertyId={propertyId} />
					))}
				</Tbody>
			</Table>
			{hiddenCount > 0 && (
				<Button size="xs" variant="link" mt={2} onClick={() => setVisibleCount(visibleCount + DOCUMENT_PAGE_SIZE)}>
					{t("documents.showMore", { count: hiddenCount })}
				</Button>
			)}
		</Box>
	);
}

function ReportsTable({ reports, propertyId }) {
	const { t } = useTranslation();
	const [visibleCount, setVisibleCount] = useState(DOCUMENT_PAGE_SIZE);
	const visible = reports.slice(0, visibleCount);
	const hiddenCount = reports.length - visible.length;

	return (
		<Box overflowX="auto">
			<Table variant="simple" size="sm">
				<Thead>
					<Tr>
						<Th pl="0px">{t("table.addedDate")}</Th>
						<Th>{t("table.name")}</Th>
						<Th>{t("table.type")}</Th>
						<Th>{t("table.number")}</Th>
						<Th>{t("table.actions")}</Th>
					</Tr>
				</Thead>
				<Tbody>
					{visible.map((row, index) => (
						<ReportsTableRow key={row.id || index} row={row} propertyId={propertyId} />
					))}
				</Tbody>
			</Table>
			{hiddenCount > 0 && (
				<Button size="xs" variant="link" mt={2} onClick={() => setVisibleCount(visibleCount + DOCUMENT_PAGE_SIZE)}>
					{t("documents.showMore", { count: hiddenCount })}
				</Button>
			)}
		</Box>
	);
}

export default function PropertyDocumentsCard({ entry, isExpanded, onToggle }) {
	const { t } = useTranslation();
	const muted = useColorModeValue("gray.600", "gray.300");
	const isEmpty = entry.leaseCount === 0 && entry.documentCount === 0 && entry.reportCount === 0;

	return (
		<Card>
			<CardHeader
				as="button"
				type="button"
				textAlign="left"
				py={3}
				aria-expanded={isExpanded}
				aria-label={isExpanded ? t("documents.collapseProperty") : t("documents.expandProperty")}
				onClick={onToggle}
			>
				<Flex alignItems="center" gap={3} flexWrap="wrap">
					<Box as={isExpanded ? ChevronDownIcon : ChevronRightIcon} boxSize={5} />
					<Text fontSize="md" fontWeight="semibold" flex="1" minW="12rem">
						{entry.address}
					</Text>
					{entry.currentLeaseCount > 0 && (
						<Badge colorScheme="green">{t("documents.inForceCount", { count: entry.currentLeaseCount })}</Badge>
					)}
					{entry.expiringSoonCount > 0 && (
						<Badge colorScheme="yellow">{t("documents.expiringSoonCount", { count: entry.expiringSoonCount })}</Badge>
					)}
					<Text fontSize="xs" color={muted}>
						{t("documents.propertySummary", {
							leases: entry.leaseCount,
							documents: entry.documentCount,
							reports: entry.reportCount,
						})}
					</Text>
				</Flex>
			</CardHeader>

			{isExpanded && (
				<CardBody pt={0}>
					<Stack spacing={3}>
						{isEmpty && (
							<Text fontSize="sm" color={muted}>
								{t("documents.noneForProperty")}
							</Text>
						)}

						{entry.currentLeases.length > 0 && (
							<DocumentsGroup
								label={t("documents.currentLease")}
								count={entry.currentLeases.length}
								colorScheme="green"
								defaultOpen
							>
								<DocumentsTable documents={entry.currentLeases} propertyId={entry.propertyId} />
							</DocumentsGroup>
						)}

						{entry.currentDocuments.length > 0 && (
							<DocumentsGroup
								label={t("documents.currentDocuments")}
								count={entry.currentDocuments.length}
								colorScheme="green"
								defaultOpen
							>
								<DocumentsTable documents={entry.currentDocuments} propertyId={entry.propertyId} />
							</DocumentsGroup>
						)}

						{entry.previousLeases.length > 0 && (
							<DocumentsGroup label={t("documents.previousLeases")} count={entry.previousLeases.length}>
								<DocumentsTable documents={entry.previousLeases} propertyId={entry.propertyId} />
							</DocumentsGroup>
						)}

						{entry.pastDocuments.length > 0 && (
							<DocumentsGroup label={t("documents.pastDocuments")} count={entry.pastDocuments.length}>
								<DocumentsTable documents={entry.pastDocuments} propertyId={entry.propertyId} />
							</DocumentsGroup>
						)}

						{entry.reports.length > 0 && (
							<DocumentsGroup label={t("documents.reports")} count={entry.reports.length}>
								<ReportsTable reports={entry.reports} propertyId={entry.propertyId} />
							</DocumentsGroup>
						)}
					</Stack>
				</CardBody>
			)}
		</Card>
	);
}
